import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, Save, Send, KeyRound, CheckCircle2, AlertCircle, ImagePlus, Trash2, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandLogo, { notifyLogoUpdated } from "@/components/BrandLogo";

const COMMON_PRESETS = [
  { label: "Gmail", host: "smtp.gmail.com", port: 587, tls: true, ssl: false, hint: "Use a Google App Password (16-char) — not your account password." },
  { label: "Microsoft 365", host: "smtp.office365.com", port: 587, tls: true, ssl: false, hint: "Modern auth may require an app password / SMTP-enabled mailbox." },
  { label: "SendGrid", host: "smtp.sendgrid.net", port: 587, tls: true, ssl: false, hint: "Username = literal 'apikey', password = your SendGrid API key." },
  { label: "Resend", host: "smtp.resend.com", port: 587, tls: true, ssl: false, hint: "Username = 'resend', password = your re_… API key." },
  { label: "AWS SES", host: "email-smtp.ap-south-1.amazonaws.com", port: 587, tls: true, ssl: false, hint: "Use the SMTP credentials generated in SES → SMTP settings." },
  { label: "Custom", host: "", port: 587, tls: true, ssl: false, hint: "" },
];

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState({
    enabled: false, smtp_host: "", smtp_port: 587, smtp_username: "",
    smtp_password: "", use_tls: true, use_ssl: false,
    from_email: "", from_name: "MPTG-CRM", reply_to: "",
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [presetIdx, setPresetIdx] = useState(5); // Custom

  useEffect(() => {
    if (user?.role !== "crm_manager") return;
    api.get("/settings/email").then(({ data }) => {
      setS((p) => ({ ...p, ...data, smtp_password: "" }));
      setPasswordSet(!!data.password_set);
    }).catch(() => {});
  }, [user?.role]);

  if (user?.role !== "crm_manager") {
    return <div className="p-8 text-slate-600" data-testid="settings-no-access">Admin only.</div>;
  }

  const applyPreset = (i) => {
    setPresetIdx(i);
    const p = COMMON_PRESETS[i];
    if (p.label !== "Custom") {
      setS({ ...s, smtp_host: p.host, smtp_port: p.port, use_tls: p.tls, use_ssl: p.ssl });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...s };
      if (!payload.smtp_password) delete payload.smtp_password; // keep existing
      const { data } = await api.put("/settings/email", payload);
      setS((p) => ({ ...p, ...data, smtp_password: "" }));
      setPasswordSet(!!data.password_set);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/email/test", { to: testTo || null });
      if (data.ok) toast.success("Test email sent — check the inbox.");
      else toast.error(`Test failed: ${data.message}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test request failed");
    } finally { setTesting(false); }
  };

  const ready = s.enabled && s.smtp_host && s.from_email && (passwordSet || s.smtp_password);

  return (
    <div className="p-6 md:p-8 max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <div className="mptg-overline">Configuration</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
          <Mail className="w-7 h-7 text-[#2E86C1]" /> Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Manage company branding and outbound email here.
        </p>
      </div>

      <LogoSection />

      <div className="pt-2">
        <h2 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#2E86C1]" /> Email / SMTP
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          When SMTP is disabled, every message still lands in <code className="text-xs bg-slate-100 px-1 rounded">email_log</code> as <em>queued</em>.
        </p>
      </div>

      <div className="mptg-card flex items-center justify-between" data-testid="smtp-status-card">
        <div className="flex items-center gap-3">
          {ready ? <CheckCircle2 className="w-5 h-5 text-[#1E8449]" /> : <AlertCircle className="w-5 h-5 text-[#F39C12]" />}
          <div>
            <div className="font-semibold">{ready ? "Outbound email is ENABLED" : "Outbound email is DISABLED"}</div>
            <div className="text-xs text-slate-500">
              {ready
                ? `Sending as ${s.from_name ? s.from_name + " <" + s.from_email + ">" : s.from_email}`
                : "Fill in the SMTP fields below, save, then click Send test."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="enabled-switch" className="text-sm text-slate-600">Enable</Label>
          <Switch id="enabled-switch" checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="smtp-enabled-switch" />
        </div>
      </div>

      <div className="mptg-card space-y-4">
        <h3 className="font-heading font-bold text-slate-800">SMTP server</h3>

        <div>
          <Label>Provider preset</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {COMMON_PRESETS.map((p, i) => (
              <button key={p.label} onClick={() => applyPreset(i)}
                className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                  presetIdx === i ? "bg-[#2E86C1] text-white border-[#2E86C1]" : "bg-white text-slate-600 border-slate-200 hover:border-[#2E86C1]"
                }`}
                data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >{p.label}</button>
            ))}
          </div>
          {COMMON_PRESETS[presetIdx].hint && (
            <div className="text-xs text-slate-500 mt-2">{COMMON_PRESETS[presetIdx].hint}</div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label>SMTP host *</Label>
            <Input value={s.smtp_host} onChange={(e) => setS({ ...s, smtp_host: e.target.value })} placeholder="smtp.example.com" data-testid="smtp-host-input" />
          </div>
          <div>
            <Label>Port *</Label>
            <Input type="number" value={s.smtp_port} onChange={(e) => setS({ ...s, smtp_port: +e.target.value })} data-testid="smtp-port-input" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Username</Label>
            <Input value={s.smtp_username} onChange={(e) => setS({ ...s, smtp_username: e.target.value })} placeholder="apikey / your-mailbox@…" data-testid="smtp-username-input" />
          </div>
          <div>
            <Label className="flex items-center gap-1">
              <KeyRound className="w-3.5 h-3.5" /> Password / API key
              {passwordSet && !s.smtp_password && <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded ml-1">saved</span>}
            </Label>
            <Input type="password" value={s.smtp_password} onChange={(e) => setS({ ...s, smtp_password: e.target.value })}
              placeholder={passwordSet ? "•••••••• (leave blank to keep)" : "••••••••"}
              data-testid="smtp-password-input" />
          </div>
        </div>

        <div className="flex items-center gap-6 pt-1">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={s.use_tls} onChange={(e) => setS({ ...s, use_tls: e.target.checked, use_ssl: e.target.checked ? false : s.use_ssl })} data-testid="smtp-tls-checkbox" />
            STARTTLS (recommended on 587)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={s.use_ssl} onChange={(e) => setS({ ...s, use_ssl: e.target.checked, use_tls: e.target.checked ? false : s.use_tls })} data-testid="smtp-ssl-checkbox" />
            SSL/TLS (port 465)
          </label>
        </div>
      </div>

      <div className="mptg-card space-y-4">
        <h3 className="font-heading font-bold text-slate-800">Sender</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>From email *</Label>
            <Input type="email" value={s.from_email} onChange={(e) => setS({ ...s, from_email: e.target.value })} placeholder="crm@yourdomain.com" data-testid="from-email-input" />
          </div>
          <div>
            <Label>From name</Label>
            <Input value={s.from_name} onChange={(e) => setS({ ...s, from_name: e.target.value })} placeholder="MAS Callnet — MPTG-CRM" data-testid="from-name-input" />
          </div>
        </div>
        <div>
          <Label>Reply-to (optional)</Label>
          <Input type="email" value={s.reply_to} onChange={(e) => setS({ ...s, reply_to: e.target.value })} placeholder="ops@yourdomain.com" data-testid="reply-to-input" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="smtp-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save settings"}
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
            placeholder="Test recipient (defaults to From email)"
            className="max-w-xs"
            data-testid="test-email-input" />
          <Button onClick={sendTest} disabled={testing || !s.smtp_host || !s.from_email} variant="outline" className="border-slate-200" data-testid="smtp-test-btn">
            <Send className="w-4 h-4 mr-1" /> {testing ? "Sending…" : "Send test"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LogoSection() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [bust, setBust] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/settings/logo?probe=${bust}`, { credentials: "include" })
      .then((r) => { if (!cancelled) setHasLogo(r.ok); })
      .catch(() => { if (!cancelled) setHasLogo(false); });
    return () => { cancelled = true; };
  }, [bust]);

  const onUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error("Logo too large. Max 1 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded");
      setBust((n) => n + 1);
      notifyLogoUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove the company logo? The text brand will be shown instead.")) return;
    setBusy(true);
    try {
      await api.delete("/settings/logo");
      toast.success("Logo removed");
      setBust((n) => n + 1);
      notifyLogoUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mptg-card space-y-4" data-testid="logo-section">
      <div className="flex items-center gap-2">
        <ImageIcon className="w-5 h-5 text-[#2E86C1]" />
        <h3 className="font-heading font-bold text-slate-800">Company logo</h3>
      </div>
      <p className="text-xs text-slate-500 -mt-2">
        Used on the login screen, sidebar header and in PDF report headers. PNG / JPG / WebP, max 1 MB. Square or wide formats render best.
      </p>
      <div className="flex items-center gap-6">
        <div className="w-24 h-24 border border-slate-200 rounded bg-white flex items-center justify-center overflow-hidden">
          <BrandLogo size={88} fallbackChar="M" />
        </div>
        <div className="space-y-2 flex-1">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onUpload}
            className="hidden"
            data-testid="logo-file-input"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="bg-[#2E86C1] hover:bg-[#2471A3] text-white"
              data-testid="upload-logo-btn"
            >
              <ImagePlus className="w-4 h-4 mr-1" />
              {hasLogo ? "Replace logo" : "Upload logo"}
            </Button>
            {hasLogo && (
              <Button
                onClick={onDelete}
                disabled={busy}
                variant="outline"
                className="border-slate-200 text-[#C0392B] hover:bg-red-50"
                data-testid="delete-logo-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Remove
              </Button>
            )}
          </div>
          <div className="text-xs text-slate-500">
            {hasLogo ? "Logo is currently active." : "No custom logo set — using the default text brand."}
          </div>
        </div>
      </div>
    </div>
  );
}
